import type { components } from '@/lib/openapi.generated'
import { isRecord, json, nullableString, request, requireBoolean, requireNumber, requireString, withQuery } from '@/lib/api'

import { decodePolicyRefreshStatus, type PolicyRefreshStatus } from '../lib/policy-authoring'

type Schemas = components['schemas']
export type PolicySummary = Omit<Schemas['PolicySummary'], 'refreshStatus'> & { refreshStatus: PolicyRefreshStatus }

export const policyCatalogKeys = {
  all: (entityId: string) => ['policy-catalog', entityId],
}

function decodePolicySummary(raw: unknown): PolicySummary {
  if (!isRecord(raw)) throw new Error('Malformed policy summary')
  return {
    id: requireString(raw.id, 'id'),
    valueType: requireString(raw.valueType, 'valueType'),
    name: requireString(raw.name, 'name'),
    selectorExpression: nullableString(raw.selectorExpression, 'selectorExpression'),
    priority: requireNumber(raw.priority, 'priority'),
    revision: requireNumber(raw.revision, 'revision'),
    refreshStatus: decodePolicyRefreshStatus(raw.refreshStatus),
    enabled: requireBoolean(raw.enabled, 'enabled'),
    supportsEnablement: requireBoolean(raw.supportsEnablement, 'supportsEnablement'),
  } satisfies PolicySummary
}

function decodePolicyCatalog(raw: unknown) {
  if (!isRecord(raw) || !Array.isArray(raw.items)) throw new Error('Malformed policy catalog')
  return {
    items: raw.items.map(decodePolicySummary),
    nextCursor: nullableString(raw.nextCursor, 'nextCursor'),
    hasMore: requireBoolean(raw.hasMore, 'hasMore'),
  } satisfies Schemas['PolicyCatalogResponse']
}

export const policyCatalogApi = {
  setEnabled: (entityId: string, valueType: string, policyId: string, expectedRevision: number, enabled: boolean) => request(
    `/api/v1/entities/${entityId}/policies/${encodeURIComponent(valueType)}/${policyId}/enabled`,
    (raw) => {
      if (!isRecord(raw)) throw new Error('Malformed policy receipt')
      return { revision: requireNumber(raw.revision, 'revision') }
    },
    json('PUT', { expectedRevision, enabled } satisfies Schemas['SetPolicyEnabledRequest']),
  ),
  list: (entityId: string, after?: string) => request(
    withQuery(`/api/v1/entities/${entityId}/policies`, { limit: '50', after }),
    decodePolicyCatalog,
  ),
}
